(() => {
  const ROOT_SELECTOR = '[data-hk-product-information]';

  const moneyText = (variant, field) => variant?.[field] || '';

  class HKProductInformation {
    constructor(root) {
      if (root.dataset.hkProductReady === 'true') return;
      root.dataset.hkProductReady = 'true';
      this.root = root;
      this.slides = [...root.querySelectorAll('[data-hkpi-slide]')];
      this.thumbnails = [...root.querySelectorAll('[data-hkpi-thumbnail]')];
      this.optionButtons = [...root.querySelectorAll('[data-hkpi-option-value]')];
      this.planCards = [...root.querySelectorAll('[data-hkpi-plan-card]')];
      this.variantInput = root.querySelector('[data-hkpi-variant-id]');
      this.quantityInput = root.querySelector('[data-hkpi-quantity]');
      this.addButton = root.querySelector('[data-hkpi-add]');
      this.addButtonText = root.querySelector('[data-hkpi-add-text]');
      this.addToCartComponent = root.querySelector('add-to-cart-component');
      this.activeSlide = Math.max(this.slides.findIndex((slide) => !slide.hidden), 0);
      this.selectedOptions = [];

      try {
        this.variants = JSON.parse(root.querySelector('[data-hkpi-variants]')?.textContent || '[]');
      } catch (error) {
        this.variants = [];
      }

      this.prepareGallery();
      this.bindGallery();
      this.bindOptions();
      this.bindQuantity();
      this.bindCartDrawer();
      this.setInitialVariant();
    }

    prepareGallery() {
      // Hidden slides must load before the customer selects them. Keep the
      // initial hero at high priority and warm the other images in the background.
      this.slides.forEach((slide) => {
        const image = slide.querySelector('[data-hkpi-main-image]') || slide.querySelector('img');
        if (!image) return;

        image.fetchPriority = slide.hidden ? 'low' : 'high';
        image.decoding = 'async';
        image.loading = 'eager';

        const ready = () => {
          if (!image.naturalWidth) return;
          const reveal = () => slide.classList.add('is-image-ready');
          if (typeof image.decode === 'function') {
            image.decode().then(reveal, reveal);
          } else {
            reveal();
          }
        };

        if (image.complete && image.naturalWidth) ready();
        else image.addEventListener('load', ready, { once: true });
      });
    }

    prioritizeSlide(index) {
      const slide = this.slides[index];
      const image = slide?.querySelector('[data-hkpi-main-image]') || slide?.querySelector('img');
      if (!image) return;
      image.fetchPriority = 'high';
      image.loading = 'eager';
    }

    bindGallery() {
      this.thumbnails.forEach((thumbnail, index) => {
        thumbnail.addEventListener('pointerenter', () => this.prioritizeSlide(index));
        thumbnail.addEventListener('focus', () => this.prioritizeSlide(index));
        thumbnail.addEventListener('click', () => {
          this.showSlide(index);
        });
      });

      this.root.querySelector('[data-hkpi-previous]')?.addEventListener('click', () => {
        this.showSlide(this.activeSlide - 1);
      });
      this.root.querySelector('[data-hkpi-next]')?.addEventListener('click', () => {
        this.showSlide(this.activeSlide + 1);
      });
    }

    showSlide(index, options = {}) {
      if (!this.slides.length) return;
      const syncThumbnail = options.syncThumbnail !== false;
      this.activeSlide = (index + this.slides.length) % this.slides.length;
      this.prioritizeSlide(this.activeSlide);
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.activeSlide;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });
      this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const active = thumbnailIndex === this.activeSlide;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (syncThumbnail) this.keepThumbnailVisible(this.thumbnails[this.activeSlide]);
    }

    keepThumbnailVisible(thumbnail) {
      const rail = thumbnail?.parentElement;
      if (!rail) return;

      const railRect = rail.getBoundingClientRect();
      const thumbnailRect = thumbnail.getBoundingClientRect();
      const inset = 4;
      let left = 0;
      let top = 0;

      if (thumbnailRect.left < railRect.left + inset) left = thumbnailRect.left - railRect.left - inset;
      else if (thumbnailRect.right > railRect.right - inset) left = thumbnailRect.right - railRect.right + inset;

      if (thumbnailRect.top < railRect.top + inset) top = thumbnailRect.top - railRect.top - inset;
      else if (thumbnailRect.bottom > railRect.bottom - inset) top = thumbnailRect.bottom - railRect.bottom + inset;

      if (!left && !top) return;

      // Update only the nested thumbnail rail. A smooth scroll here can be
      // promoted to the page viewport by Safari/Chrome scroll anchoring while
      // the customer is scrolling, which makes the whole page jump back to
      // this section.
      if (left) rail.scrollLeft += left;
      if (top) rail.scrollTop += top;
    }

    showMedia(mediaId) {
      if (!mediaId) return;
      const index = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (index >= 0) this.showSlide(index);
    }

    bindOptions() {
      this.optionButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const position = Number(button.dataset.optionPosition) - 1;
          const value = button.dataset.hkpiOptionValue;
          const requested = [...this.selectedOptions];
          requested[position] = value;

          let variant = this.variants.find((item) => item.options.every((option, index) => option === requested[index]));
          variant ||= this.variants.find((item) => item.available && item.options[position] === value);
          variant ||= this.variants.find((item) => item.options[position] === value);
          if (variant) this.selectVariant(variant, true);
        });
      });
    }

    setInitialVariant() {
      const initialId = Number(this.variantInput?.value);
      const initialVariant = this.variants.find((variant) => Number(variant.id) === initialId) || this.variants[0];
      if (initialVariant) this.selectVariant(initialVariant, false);
    }

    selectVariant(variant, updateUrl) {
      // Switch media immediately, before notifying other product components.
      this.showMedia(variant.featured_media_id);
      this.selectedOptions = [...variant.options];
      this.optionButtons.forEach((button) => {
        const position = Number(button.dataset.optionPosition) - 1;
        const selected = variant.options[position] === button.dataset.hkpiOptionValue;
        const possible = this.variants.some((item) => item.available && item.options.every((option, index) => (
          index === position ? option === button.dataset.hkpiOptionValue : option === variant.options[index]
        )));
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', String(selected));
        button.disabled = !possible && !selected;
      });
      this.updatePlanCards();

      if (this.variantInput) {
        this.variantInput.value = variant.id;
        this.variantInput.disabled = !variant.available;
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const price = this.root.querySelector('[data-hkpi-price]');
      const compare = this.root.querySelector('[data-hkpi-compare]');
      const discount = this.root.querySelector('[data-hkpi-discount]');
      if (price) price.textContent = moneyText(variant, 'price_formatted');
      if (compare) {
        compare.textContent = variant.compare_at_formatted || '';
        compare.hidden = !(variant.compare_at_price > variant.price);
      }
      if (discount) {
        const percentage = variant.compare_at_price > variant.price
          ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)
          : 0;
        discount.textContent = percentage ? `${percentage}% ${this.root.dataset.offLabel}` : '';
        discount.hidden = !percentage;
      }

      if (this.addButton) this.addButton.disabled = !variant.available;
      if (this.addButtonText) {
        this.addButtonText.textContent = variant.available
          ? this.root.dataset.addLabel
          : this.root.dataset.soldOutLabel;
      }
      const availability = this.root.querySelector('[data-hkpi-availability]');
      if (availability) {
        availability.textContent = variant.available ? this.root.dataset.availableLabel : this.root.dataset.soldOutLabel;
        availability.classList.toggle('is-sold-out', !variant.available);
      }

      if (this.quantityInput) {
        this.quantityInput.min = variant.quantity_min || 1;
        this.quantityInput.step = variant.quantity_increment || 1;
        if (variant.quantity_max) this.quantityInput.max = variant.quantity_max;
        else this.quantityInput.removeAttribute('max');
        this.quantityInput.value = Math.max(Number(this.quantityInput.value) || 1, Number(this.quantityInput.min));
      }

      if (this.addToCartComponent && variant.featured_image_url) {
        this.addToCartComponent.dataset.productVariantMedia = variant.featured_image_url;
      }
      if (updateUrl && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    updatePlanCards() {
      this.planCards.forEach((card) => {
        const position = Number(card.dataset.optionPosition) - 1;
        const requested = [...this.selectedOptions];
        requested[position] = card.dataset.hkpiOptionValue;
        const planVariant = this.variants.find((item) => item.options.every((option, index) => option === requested[index]));
        const price = card.querySelector('[data-hkpi-plan-price]');
        const compare = card.querySelector('[data-hkpi-plan-compare]');
        const badge = card.querySelector('[data-hkpi-plan-badge]');
        const discountValue = card.querySelector('[data-hkpi-plan-discount]');
        const saving = card.querySelector('[data-hkpi-plan-saving]');
        const discount = planVariant?.compare_at_price > planVariant?.price
          ? Math.round(((planVariant.compare_at_price - planVariant.price) * 100) / planVariant.compare_at_price)
          : 0;

        if (price) price.textContent = planVariant?.price_formatted || '';
        if (compare) {
          compare.textContent = planVariant?.compare_at_formatted || '';
          compare.hidden = !discount;
        }
        if (discountValue) discountValue.textContent = discount;
        if (badge) badge.hidden = !discount;
        if (saving) {
          saving.textContent = discount ? `${discount}% ${this.root.dataset.offLabel}` : '';
          saving.hidden = !discount;
        }
        card.disabled = !planVariant?.available;
        card.setAttribute('aria-disabled', String(!planVariant?.available));
      });
    }

    bindQuantity() {
      this.root.querySelector('[data-hkpi-quantity-minus]')?.addEventListener('click', () => this.changeQuantity(-1));
      this.root.querySelector('[data-hkpi-quantity-plus]')?.addEventListener('click', () => this.changeQuantity(1));
      this.quantityInput?.addEventListener('change', () => this.normalizeQuantity());
    }

    changeQuantity(direction) {
      if (!this.quantityInput) return;
      const step = Number(this.quantityInput.step) || 1;
      this.quantityInput.value = Number(this.quantityInput.value || 1) + direction * step;
      this.normalizeQuantity();
    }

    normalizeQuantity() {
      if (!this.quantityInput) return;
      const min = Number(this.quantityInput.min) || 1;
      const max = Number(this.quantityInput.max) || Infinity;
      this.quantityInput.value = Math.min(max, Math.max(min, Number(this.quantityInput.value) || min));
    }

    bindCartDrawer() {
      this.root.addEventListener('shopify:cart:lines-update', (event) => {
        if (event.action !== 'add' || !(event.target instanceof Element) || !event.target.closest(ROOT_SELECTOR)) return;
        const openDrawer = () => {
          const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
          if (trigger && trigger.getAttribute('aria-expanded') !== 'true') trigger.click();
        };
        if (event.promise) {
          event.promise.then(({ detail }) => {
            if (!detail?.didError) window.setTimeout(openDrawer, 120);
          }).catch(() => {});
        } else window.setTimeout(openDrawer, 120);
      });
    }

    destroy() {
      this.root?.removeAttribute('data-hk-product-ready');
    }
  }

  const instances = new WeakMap();
  const init = (scope = document) => {
    const roots = scope.matches?.(ROOT_SELECTOR) ? [scope] : [...scope.querySelectorAll?.(ROOT_SELECTOR) || []];
    roots.forEach((root) => {
      if (root.dataset.hkProductReady === 'true') return;
      const instance = new HKProductInformation(root);
      instances.set(root, instance);
    });
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR) ? event.target : event.target.querySelector?.(ROOT_SELECTOR);
    instances.get(root)?.destroy();
  });
})();
